import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { UserRole, UserPermissions } from '@/types/auth';
import { LoadingState } from '@/components/common/LoadingState';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requiredModule?: keyof UserPermissions;
  requiredAction?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiredModule,
  requiredAction = 'view',
}) => {
  const { user, profile, loading, isAdmin, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingState fullscreen message="Authenticating session..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admins always have unrestricted access
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check role-level requirement
  if (allowedRoles && profile?.role && !allowedRoles.includes(profile.role)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div className="max-w-md w-full bg-white border border-amber-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Access Restricted</h2>
          <p className="text-xs text-slate-600">
            This module requires higher role privileges. Your current role is{' '}
            <strong className="text-slate-900 uppercase font-mono">{profile.role}</strong>.
          </p>
          <Button variant="default" onClick={() => window.history.back()} className="w-full text-xs">
            Return to Previous Page
          </Button>
        </div>
      </div>
    );
  }

  // Check module-level JSONB requirement
  if (requiredModule && !hasPermission(requiredModule, requiredAction)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div className="max-w-md w-full bg-white border border-amber-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Module Access Restricted</h2>
          <p className="text-xs text-slate-600">
            Your user profile does not have permission to access the{' '}
            <strong className="text-slate-900 font-mono">{requiredModule}</strong> module. Please contact your system administrator to adjust your permissions.
          </p>
          <Button variant="default" onClick={() => window.history.back()} className="w-full text-xs">
            Return to Previous Page
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
